<template>
  <div class="page">
    <DistributionToolbar
      :totalPages="totalPages"
      :currentPage="currentPage"
      :filterOpen="showFilters"
      :highlightMode="highlightMode"
      :highlightPalette="highlightPalette"
      :orders="orders"
      :sorting="tableState.sorting"
      :columnOrder="tableState.columnOrder"
      :columnVisibility="tableState.columnVisibility"
      :exportLoading="exportPending"
      :scale="scale"
      :totalItems="totalItems"
      @pageChanged="setPage"
      @highlightChanged="setHighlight"
      @sortingChanged="setSorting"
      @columnOrderChanged="tableState.columnOrder = $event"
      @columnVisibilityChanged="tableState.columnVisibility = $event"
      @resetSettings="resetTableSettings"
      @scaleChanged="scale = $event"
      @export="exportOrders"
      @toggleFilter="toggleFilters"
    />

    <div v-if="!hasTelegramAccount" class="tg-warning">
      Для действий с распределением нужно привязать Telegram в настройках пользователя.
    </div>

    <div class="page-layout" :class="{ 'has-filters': showFilters }">
      <div class="table-area">
        <DistributionTable
          :orders="orders"
          :isLoading="loading"
          :isRefreshing="isFetching"
          :isPlaceholderData="isPlaceholderData"
          :highlightMode="highlightMode"
          :palette="highlightPalette"
          :autoExpandSingle="singleViewActive"
          :sorting="tableState.sorting"
          :columnOrder="tableState.columnOrder"
          :columnVisibility="tableState.columnVisibility"
          :columnSizing="tableState.columnSizing"
          :pageCount="totalPages"
          :currentPage="currentPage"
          :currentUser="currentUser"
          :permissions="permissions"
          :operationLoadingUid="operationLoadingUid"
          :runOperation="runOperation"
          :scale="scale"
          @sortingChanged="setSorting"
          @columnSizingChanged="tableState.columnSizing = $event"
          @baseRateSaved="invalidateOrders"
          @advanceCompleted="invalidateOrders"
        />
        <div v-if="hasError && orders.length === 0" class="error-block">
          <p>Ошибка загрузки данных. Попробуйте позже.</p>
        </div>
      </div>

      <aside v-if="showFilters" class="filters-sidebar">
        <DistributionFilters
          :filters="effectiveFilters"
          :hasManualStatusPermission="permissions.manualStatus"
          @change="onFilterChange"
          @clear="onClearFilters"
        />
      </aside>
    </div>
  </div>
</template>

<script>
import { useStore } from 'vuex';
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import DistributionTable from './components/DistributionTable.vue';
import DistributionFilters from './components/DistributionFilters.vue';
import DistributionToolbar from './components/DistributionToolbar.vue';
import { useUserTableScale } from '@/components/UI/useUserTableScale';
import { useDistributionPage } from './useDistributionPage';

export default {
  name: 'DistributionPage',
  components: { DistributionTable, DistributionFilters, DistributionToolbar },
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const showFilters = ref(false);
    const routeTripId = computed(() => String(
      route.params.tripId || route.query.tripId || '',
    ).trim());
    const singleViewActive = computed(() => Boolean(routeTripId.value));
    const page = useDistributionPage(routeTripId);
    const { scale, resetScale } = useUserTableScale('distribution');
    const currentUser = computed(() => store.state.user?.user || {});
    const permissions = computed(() => ({
      admin: Boolean(store.getters.hasPermission('distribution.admin')),
      advance: Boolean(store.getters.hasPermission('distribution.admin') || store.getters.hasPermission('advance.admin')),
      manualStatus: Boolean(
        store.getters.hasPermission('distribution.admin')
        || store.getters.hasPermission('distribution.admin.status_override'),
      ),
      write: Boolean(store.getters.hasPermission('distribution.write')),
      approvals: Boolean(store.getters.hasPermission('distribution.approvals')),
      baseRates: Boolean(store.getters.hasPermission('memory.base_rates')),
    }));

    const toggleFilters = () => {
      showFilters.value = !showFilters.value;
    };

    const leaveSingleView = () => routeTripId.value
      ? router.replace({ name: 'Distribution' }).catch(() => {})
      : Promise.resolve();
    const onFilterChange = (patch) => {
      leaveSingleView().then(() => page.updateFilters(patch));
    };
    const onClearFilters = () => {
      leaveSingleView().then(page.clearFilters);
    };

    watch(routeTripId, () => { showFilters.value = false; });
    const resetTableSettings = () => {
      page.resetTableState();
      resetScale();
    };

    return {
      showFilters,
      toggleFilters,
      hasError: computed(() => page.ordersQuery.isError.value),
      isFetching: computed(() => page.ordersQuery.isFetching.value),
      isPlaceholderData: computed(() => page.ordersQuery.isPlaceholderData.value),
      orders: computed(() => (Array.isArray(page.ordersQuery.data.value?.items) ? page.ordersQuery.data.value.items : [])),
      loading: computed(() => page.ordersQuery.isPending.value),
      currentPage: computed(() => page.effectiveFilters.value.page || 1),
      totalPages: computed(() => page.ordersQuery.data.value?.total_pages || 1),
      totalItems: computed(() => Number(page.ordersQuery.data.value?.total_items) || 0),
      singleViewActive,
      highlightMode: computed(() => page.filters.highlightMode),
      highlightPalette: computed(() => page.filters.highlightPalette),
      hasTelegramAccount: computed(() => Boolean(currentUser.value.tg_username)),
      currentUser,
      permissions,
      scale,
      resetTableSettings,
      onFilterChange,
      onClearFilters,
      ...page,
    };
  },
};
</script>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.page-header {
  padding: 12px 20px 4px;
}

.page-title {
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  color: #333;
}

.page-layout {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.page-layout.has-filters .table-area {
  flex: 1;
}

.table-area {
  flex: 1;
  overflow: auto;
  padding: 0 12px 12px;
}

.filters-sidebar {
  width: 280px;
  flex-shrink: 0;
  border-left: 1px solid #e5e7eb;
  overflow-y: auto;
  background: #fafafa;
}

.error-block {
  text-align: center;
  padding: 40px;
  color: #dc3545;
  font-size: 14px;
}

.tg-warning {
  margin: 12px 10px;
  padding: 8px 12px;
  border: 1px solid #ffe08a;
  border-radius: 6px;
  background: #fff8db;
  color: #7a5b00;
  font-size: 13px;
}
</style>
